import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@/components';

function CalendarIcon({ className = 'w-4 h-4', ...props }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="18"
        rx="2"
        ry="2"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="16"
        y1="2"
        x2="16"
        y2="6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="8"
        y1="2"
        x2="8"
        y2="6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="3"
        y1="10"
        x2="21"
        y2="10"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckSquareIcon({ className = 'w-4 h-4', ...props }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <polyline
        points="9 11 12 14 22 4"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ListIcon({ className = 'w-4 h-4', ...props }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <line
        x1="8"
        y1="6"
        x2="21"
        y2="6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="8"
        y1="12"
        x2="21"
        y2="12"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="8"
        y1="18"
        x2="21"
        y2="18"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="3"
        y1="6"
        x2="3.01"
        y2="6"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="3"
        y1="12"
        x2="3.01"
        y2="12"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="3"
        y1="18"
        x2="3.01"
        y2="18"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MessageCircleIcon({ className = 'w-4 h-4', ...props }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClipboardIcon({ className = 'w-4 h-4', ...props }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect
        x="8"
        y="2"
        width="8"
        height="4"
        rx="1"
        ry="1"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 12h6M9 16h6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function stripMarkdown(text) {
  if (!text) return '';
  return String(text)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
}

function HighlightText({ text, highlight }) {
  if (!text) return null;
  if (!highlight || !highlight.trim()) {
    return <span>{text}</span>;
  }
  const cleanHighlight = highlight
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${cleanHighlight})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.trim().toLowerCase() ? (
          <mark
            key={i}
            className="bg-[#43B02A]/20 text-[#1E2340] font-semibold rounded px-0.5"
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </span>
  );
}

export function SearchNodeModal({
  isOpen,
  onClose,
  steps = [],
  onSelectStep,
  selectedStepOrder = null,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setActiveFilter('all');
      setHighlightedIndex(0);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const sortedSteps = useMemo(() => {
    return [...steps].sort((a, b) => a.order - b.order);
  }, [steps]);

  // Compute filter counts
  const filterCounts = useMemo(() => {
    const counts = {
      all: sortedSteps.length,
      list: 0,
      message: 0,
      datetime: 0,
      confirmation: 0,
      quotation: 0,
      sheet: 0,
    };
    sortedSteps.forEach((s) => {
      if (s.is_google_sheet_step) counts.sheet++;
      else if (s.is_quotation_step) counts.quotation++;
      else if (s.is_datetime_step) counts.datetime++;
      else if (s.is_confirmation_step) counts.confirmation++;
      else if (s.options && s.options.length > 0) counts.list++;
      else counts.message++;
    });
    return counts;
  }, [sortedSteps]);

  // Filter and search logic
  const filteredSteps = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const cleanQ = q.replace(/^step\s*|^order\s*|^#\s*/i, '');

    return sortedSteps.filter((step) => {
      // Filter by type
      if (
        activeFilter === 'list' &&
        (!step.options || step.options.length === 0)
      ) {
        return false;
      }
      if (activeFilter === 'datetime' && !step.is_datetime_step) {
        return false;
      }
      if (activeFilter === 'confirmation' && !step.is_confirmation_step) {
        return false;
      }
      if (activeFilter === 'quotation' && !step.is_quotation_step) {
        return false;
      }
      if (activeFilter === 'sheet' && !step.is_google_sheet_step) {
        return false;
      }
      if (
        activeFilter === 'message' &&
        (step.is_quotation_step ||
          step.is_datetime_step ||
          step.is_confirmation_step ||
          step.is_google_sheet_step ||
          (step.options && step.options.length > 0))
      ) {
        return false;
      }

      // list filter should exclude special step types
      if (
        activeFilter === 'list' &&
        (step.is_quotation_step ||
          step.is_datetime_step ||
          step.is_confirmation_step ||
          step.is_google_sheet_step)
      ) {
        return false;
      }

      // If no search query, keep
      if (!q) return true;

      // Match step order / number
      const orderStr = String(step.order);
      if (orderStr === q || orderStr === cleanQ) return true;
      if (`step ${orderStr}`.includes(q) || `order ${orderStr}`.includes(q))
        return true;

      // Match question / title text
      const cleanTitle = stripMarkdown(step.question_text || '').toLowerCase();
      if (cleanTitle.includes(q)) return true;

      // Match quotation catalog title
      if (
        step.quotation_title &&
        String(step.quotation_title).toLowerCase().includes(q)
      ) {
        return true;
      }

      // Match options text
      if (step.options && step.options.length > 0) {
        const optionMatch = step.options.some((opt) => {
          const optText = (opt.text || '').toLowerCase();
          const optNum = String(opt.number || '');
          return (
            optText.includes(q) ||
            optNum === q ||
            (opt.tags || []).some((t) =>
              (t.name || t || '').toLowerCase().includes(q),
            )
          );
        });
        if (optionMatch) return true;
      }

      // Match step internal tags
      if (step.tags && step.tags.length > 0) {
        const tagMatch = step.tags.some((t) =>
          (t.name || t || '').toLowerCase().includes(q),
        );
        if (tagMatch) return true;
      }

      return false;
    });
  }, [sortedSteps, searchQuery, activeFilter]);

  // Reset highlight index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredSteps.length, searchQuery, activeFilter]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredSteps.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : Math.max(0, filteredSteps.length - 1),
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredSteps[highlightedIndex]) {
        handleSelect(filteredSteps[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSelect = (step) => {
    onSelectStep?.(step.order);
    onClose();
  };

  const getStepTypeInfo = (step) => {
    if (step.is_google_sheet_step) {
      return {
        label: 'Google Sheet',
        icon: <ClipboardIcon className="w-3.5 h-3.5 text-blue-600" />,
        badgeBg: 'bg-blue-50 text-blue-700 border-blue-200',
      };
    }
    if (step.is_quotation_step) {
      return {
        label: 'Quotation',
        icon: <ClipboardIcon className="w-3.5 h-3.5 text-teal-600" />,
        badgeBg: 'bg-teal-50 text-teal-700 border-teal-200',
      };
    }
    if (step.is_datetime_step) {
      return {
        label: 'Date & Time',
        icon: <CalendarIcon className="w-3.5 h-3.5 text-blue-500" />,
        badgeBg: 'bg-blue-50 text-blue-700 border-blue-200',
      };
    }
    if (step.is_confirmation_step) {
      return {
        label: 'Confirmation',
        icon: <CheckSquareIcon className="w-3.5 h-3.5 text-amber-500" />,
        badgeBg: 'bg-amber-50 text-amber-700 border-amber-200',
      };
    }
    if (step.options && step.options.length > 0) {
      return {
        label: `List Options (${step.options.length})`,
        icon: <ListIcon className="w-3.5 h-3.5 text-[#43B02A]" />,
        badgeBg: 'bg-[#43B02A]/10 text-[#43B02A] border-[#43B02A]/30',
      };
    }
    return {
      label: step.is_open_ended ? 'Open Ended' : 'Message',
      icon: <MessageCircleIcon className="w-3.5 h-3.5 text-purple-500" />,
      badgeBg: 'bg-purple-50 text-purple-700 border-purple-200',
    };
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      showClose={true}
      className="p-0 overflow-hidden !rounded-2xl"
      panelClassName="!p-0 !max-h-[85vh] flex flex-col"
    >
      {/* Top Search Bar */}
      <div className="p-4 sm:p-5 border-b border-border-default bg-white shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#43B02A]/10 flex items-center justify-center text-[#43B02A]">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="7" strokeWidth="2" />
                <path
                  strokeWidth="2"
                  strokeLinecap="round"
                  d="M20 20l-3.5-3.5"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1E2340]">
                Search Steps & Nodes
              </h3>
              <p className="text-[11px] text-text-secondary">
                Search by step number, question text, options, or tags
              </p>
            </div>
          </div>
        </div>

        {/* Input box */}
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="7" strokeWidth="2" />
              <path strokeWidth="2" strokeLinecap="round" d="M20 20l-3.5-3.5" />
            </svg>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type step number (e.g. 86), question text, option..."
            className="w-full h-11 pl-10 pr-10 text-sm bg-[#F5F5F5] border border-transparent focus:border-[#43B02A] focus:bg-white rounded-xl text-[#1E2340] placeholder:text-text-muted focus:outline-none transition-all shadow-inner"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary p-1 rounded-md hover:bg-slate-200/60 transition-colors"
              title="Clear search"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <line
                  x1="18"
                  y1="6"
                  x2="6"
                  y2="18"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 mt-3 overflow-x-auto pb-1 text-xs no-scrollbar">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 ${
              activeFilter === 'all'
                ? 'bg-[#1E2340] text-white shadow-sm'
                : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
            }`}
          >
            All ({filterCounts.all})
          </button>
          <button
            onClick={() => setActiveFilter('list')}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1 ${
              activeFilter === 'list'
                ? 'bg-[#43B02A] text-white shadow-sm'
                : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
            }`}
          >
            List Options ({filterCounts.list})
          </button>
          <button
            onClick={() => setActiveFilter('message')}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1 ${
              activeFilter === 'message'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
            }`}
          >
            Messages ({filterCounts.message})
          </button>
          <button
            onClick={() => setActiveFilter('datetime')}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1 ${
              activeFilter === 'datetime'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
            }`}
          >
            Date & Time ({filterCounts.datetime})
          </button>
          <button
            onClick={() => setActiveFilter('quotation')}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1 ${
              activeFilter === 'quotation'
                ? 'bg-teal-600 text-white shadow-sm'
                : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
            }`}
          >
            Quotation ({filterCounts.quotation})
          </button>
          <button
            onClick={() => setActiveFilter('confirmation')}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1 ${
              activeFilter === 'confirmation'
                ? 'bg-amber-600 text-white shadow-sm'
                : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
            }`}
          >
            Confirmation ({filterCounts.confirmation})
          </button>
          <button
            onClick={() => setActiveFilter('sheet')}
            className={`px-2.5 py-1 rounded-lg font-medium transition-all shrink-0 flex items-center gap-1 ${
              activeFilter === 'sheet'
                ? 'bg-blue-700 text-white shadow-sm'
                : 'bg-slate-100 text-text-secondary hover:bg-slate-200'
            }`}
          >
            Google Sheet ({filterCounts.sheet})
          </button>
        </div>
      </div>

      {/* Results List */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 max-h-[50vh] custom-scrollbar bg-[#F8FAFC]"
      >
        {filteredSteps.length === 0 ? (
          <div className="py-12 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3 text-text-muted">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="7" strokeWidth="1.5" />
                <path
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  d="M20 20l-3.5-3.5"
                />
              </svg>
            </div>
            <p className="text-sm font-semibold text-text-primary">
              No matching steps found
            </p>
            <p className="text-xs text-text-muted mt-1 max-w-sm mx-auto">
              No steps match &quot;{searchQuery}&quot;. Try searching with a
              different keyword or step number.
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 px-3 py-1.5 text-xs font-medium bg-white border border-border-default rounded-lg hover:bg-slate-50 text-text-primary shadow-sm"
              >
                Clear search query
              </button>
            )}
          </div>
        ) : (
          filteredSteps.map((step, idx) => {
            const typeInfo = getStepTypeInfo(step);
            const isSelected = selectedStepOrder === step.order;
            const isHighlighted = highlightedIndex === idx;

            return (
              <div
                key={`step_${step.order}_${step.id || idx}`}
                onClick={() => handleSelect(step)}
                onMouseEnter={() => setHighlightedIndex(idx)}
                className={`group p-3 sm:p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col gap-2 ${
                  isHighlighted || isSelected
                    ? 'bg-white border-[#43B02A] shadow-md ring-2 ring-[#43B02A]/20'
                    : 'bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                {/* Header line: Step order + Type badge + Next step */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-md bg-[#1E2340] text-white text-[11px] font-bold tracking-wide">
                      Step {step.order}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${typeInfo.badgeBg}`}
                    >
                      {typeInfo.icon}
                      <span>{typeInfo.label}</span>
                    </span>
                  </div>

                  <div className="flex items-center gap-1 text-[11px] text-text-muted group-hover:text-text-primary">
                    {step.next_step_order ? (
                      <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-700 font-medium">
                        → Step {step.next_step_order}
                      </span>
                    ) : step.options && step.options.length > 0 ? (
                      <span className="text-[#43B02A] font-medium">
                        {step.options.filter((o) => !!o.next_step_order).length}{' '}
                        / {step.options.length} branched
                      </span>
                    ) : (
                      <span className="text-slate-400">Ends Flow</span>
                    )}
                    <span className="opacity-0 group-hover:opacity-100 text-[#43B02A] font-bold transition-opacity ml-1">
                      Jump ➔
                    </span>
                  </div>
                </div>

                {/* Question / Title Text */}
                <div className="text-xs sm:text-sm font-medium text-[#1E2340] line-clamp-2 leading-relaxed">
                  <HighlightText
                    text={stripMarkdown(step.question_text) || 'Untitled step'}
                    highlight={searchQuery}
                  />
                </div>

                {/* Options preview pills if available */}
                {step.options && step.options.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1 border-t border-slate-100">
                    {step.options.slice(0, 8).map((opt, optIdx) => (
                      <span
                        key={optIdx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-50 border border-slate-200 text-[11px] text-text-secondary max-w-[200px] truncate"
                      >
                        <span className="w-3.5 h-3.5 rounded-full bg-[#43B02A]/15 text-[#43B02A] text-[9px] font-bold flex items-center justify-center shrink-0">
                          {opt.number || optIdx + 1}
                        </span>
                        <span className="truncate">
                          <HighlightText
                            text={opt.text || `Option ${opt.number}`}
                            highlight={searchQuery}
                          />
                        </span>
                        {opt.next_step_order && (
                          <span className="text-[10px] text-slate-400 shrink-0">
                            → {opt.next_step_order}
                          </span>
                        )}
                      </span>
                    ))}
                    {step.options.length > 8 && (
                      <span className="text-[11px] text-text-muted self-center px-1">
                        +{step.options.length - 8} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer Info */}
      <div className="p-3 px-4 border-t border-border-default bg-white flex items-center justify-between text-xs text-text-muted shrink-0">
        <span>
          Showing <strong>{filteredSteps.length}</strong> of{' '}
          <strong>{steps.length}</strong> steps
        </span>
      </div>
    </Modal>
  );
}

export default SearchNodeModal;
